// PreferencesCard — non-notification global prefs: units, week start,
// snooze default. The app is light-only, so there is no theme control.

import { useState } from 'react';
import { api, APIError } from '../lib/api';
import type { UserSettings, UserSettingsPatch } from '../lib/api';
import { Card } from './Card';

export function PreferencesCard({
  settings,
  onChange,
}: {
  settings: UserSettings;
  onChange: (next: UserSettings) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (patch: UserSettingsPatch) => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.patchUserSettings(patch);
      onChange(next);
    } catch (e) {
      setError(e instanceof APIError ? `Save: ${e.status}` : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Preferences" hint="Display & default behavior across the app.">
      <Row label="Units">
        <Segmented
          value={settings.units}
          options={[
            { value: 'metric', label: 'kg / km' },
            { value: 'imperial', label: 'lb / mi' },
          ]}
          disabled={busy}
          onChange={(v) => save({ units: v as 'metric' | 'imperial' })}
        />
      </Row>
      <Row label="Week starts">
        <Segmented
          value={settings.week_start}
          options={[
            { value: 'monday', label: 'Mon' },
            { value: 'sunday', label: 'Sun' },
          ]}
          disabled={busy}
          onChange={(v) => save({ week_start: v as 'monday' | 'sunday' })}
        />
      </Row>
      <Row label="Snooze">
        <Segmented
          value={String(settings.snooze_default_minutes)}
          options={[
            { value: '5', label: '5m' },
            { value: '10', label: '10m' },
            { value: '15', label: '15m' },
            { value: '30', label: '30m' },
          ]}
          disabled={busy}
          onChange={(v) => save({ snooze_default_minutes: Number(v) })}
        />
      </Row>
      {error && <p className="text-xs text-danger">{error}</p>}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-sm text-fg">{label}</span>
      {children}
    </div>
  );
}

function Segmented({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex gap-1 rounded-full bg-bg-sunken p-1">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            disabled={disabled}
            className={`rounded-full px-2.5 py-2.5 text-xs font-mono uppercase tracking-[0.1em] transition-colors ${
              on ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
